import type { ReactNode } from 'react';
import { Button } from '@gokrew/ui';
import type { ButtonProps } from '@gokrew/models/button';
import { ArrowUpRight, Inbox } from 'lucide-react';
import { useCrmFormat } from './crm-format';
import styles from './Crm.module.css';
/** The CRM's compact button: same variants as Button, smaller and lighter. */
export const CrmButton = ({ className, ...props }: ButtonProps) => (
  <Button
    size="sm"
    {...props}
    className={className ? `${styles.button} ${className}` : styles.button}
  />
);
/** The asterisk after a mandatory field's label. */
export const CrmRequired = () => (
  <span className={styles.requiredMark} aria-hidden="true">
    *
  </span>
);
export const CrmEmpty = ({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) => (
  <div className={styles.empty}>
    <span className={styles.emptyIcon}>
      <Inbox />
    </span>
    <h2>{title}</h2>
    {description && <p>{description}</p>}
    {action}
  </div>
);
export const CrmError = ({ retry }: { retry: () => void }) => {
  const { t } = useCrmFormat();
  return (
    <div className={styles.errorBox} role="alert">
      <p>{t('crm.load_error')}</p>
      <CrmButton variant="outline" onClick={retry}>
        {t('crm.retry')}
      </CrmButton>
    </div>
  );
};
export const CrmPagination = ({
  page,
  total,
  pageSize = 50,
  onPage,
}: {
  page: number;
  total: number;
  pageSize?: number;
  onPage: (page: number) => void;
}) => {
  const { t } = useCrmFormat();
  if (total <= pageSize) return null;
  return (
    <div className={styles.pagination}>
      <span>
        {t('crm.page', {
          page,
          pages: Math.max(1, Math.ceil(total / pageSize)),
        })}
      </span>
      <CrmButton
        variant="outline"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >
        {t('crm.previous')}
      </CrmButton>
      <CrmButton
        variant="outline"
        disabled={page * pageSize >= total}
        onClick={() => onPage(page + 1)}
      >
        {t('crm.next')}
      </CrmButton>
    </div>
  );
};
export const CrmSectionTitle = ({
  title,
  action,
  onAction,
}: {
  title: string;
  action?: string;
  onAction?: () => void;
}) => (
  <div className={styles.sectionHeading}>
    <h2>{title}</h2>
    {action && (
      <CrmButton variant="ghost" IconSuffix={ArrowUpRight} onClick={onAction}>
        {action}
      </CrmButton>
    )}
  </div>
);
